import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { FaSearch, FaUtensils, FaHamburger, FaIceCream, FaGlassWhiskey, FaEdit, FaToggleOn, FaToggleOff, FaSpinner, FaExclamationTriangle, FaCheckCircle, FaEye, FaEyeSlash, FaImage, FaTimes, FaSave, FaEuroSign } from 'react-icons/fa';
import './ViewMenuServer.css';

const ViewMenuServer = ({ menuId }) => {
  const [categorie, setCategorie] = useState('');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Fonctions utilitaires
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'entrees': return FaUtensils;
      case 'plats': return FaHamburger;
      case 'desserts': return FaIceCream;
      case 'boissons': return FaGlassWhiskey;
      default: return FaUtensils;
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'entrees': return 'Entrées';
      case 'plats': return 'Plats';
      case 'desserts': return 'Desserts';
      case 'boissons': return 'Boissons';
      default: return category;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'entrees': return '#10b981';
      case 'plats': return '#f59e0b';
      case 'desserts': return '#ef4444';
      case 'boissons': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const handleToggleActive = async (item) => {
    try {
      setLoading(true);
      const updatedState = !item.actif;
      const itemRef = doc(db, `menu/${menuId}/${categorie}`, item.id);
      await updateDoc(itemRef, { actif: updatedState });
      setConfirmation(`Élément ${updatedState ? 'activé' : 'désactivé'} avec succès !`);
      triggerPopup();
    } catch (error) {
      setError('Erreur lors du changement d\'état');
    } finally {
      setLoading(false);
    }
  };  

  const handleFieldUpdate = async (item, field, value) => {
    try {
      setLoading(true);
      const itemRef = doc(db, `menu/${menuId}/${categorie}`, item.id);
      await updateDoc(itemRef, { [field]: value });
      setConfirmation('Champ mis à jour avec succès !');
      triggerPopup();
    } catch (error) {
      setError('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditValue(item.nom);
  };

  const handleSaveEdit = async (item, field) => {
    if (editValue.trim()) {
      await handleFieldUpdate(item, field, editValue);
      setEditingItem(null);
      setEditValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditValue('');
  };  

  const triggerPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  const listenToItems = useCallback((cat) => {
    const itemsCollection = collection(db, `menu/${menuId}/${cat}`);
    
    return onSnapshot(itemsCollection, (snapshot) => {
      const itemList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setItems(itemList);
      setFilteredItems(itemList);
      setError('');
    }, () => {
      setError('Erreur lors du chargement des éléments');
    });
  }, [menuId]);

  const handleCategoryChange = useCallback((cat) => {
    setCategorie(cat);
    // Au lieu d'appeler fetchItems, on utilise listenToItems qui configure un écouteur
  }, []);

  useEffect(() => {
    if (categorie) {
      const unsubscribe = listenToItems(categorie);
      // Nettoyer l'écouteur précédent lors du changement de catégorie
      return () => unsubscribe();
    }
  }, [categorie, listenToItems]);  

  useEffect(() => {
    handleCategoryChange('entrees');
  }, [handleCategoryChange]);

  const fetchAllItems = useCallback(async () => {
    try {
      const menuCategories = ['entrees', 'plats', 'desserts', 'boissons'];
      let allItems = [];
      
      for (const cat of menuCategories) {
        const itemsCollection = collection(db, `menu/${menuId}/${cat}`);
        const itemSnapshot = await getDocs(itemsCollection);
        const itemList = itemSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          category: cat
        }));
        allItems = [...allItems, ...itemList];
      }
      
      return allItems;
    } catch (error) {
      return [];
    }
  }, [menuId]);

  useEffect(() => {
    if (searchTerm) {
      fetchAllItems().then(allItems => {
        const filtered = allItems.filter(item => 
          item.nom.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredItems(filtered);
      });
    } else {
      setFilteredItems(items);
    }
  }, [searchTerm, items, fetchAllItems]);

  return (
    <div className="view-menu-server-container">
      {/* Header */}
      <div className="view-menu-server-header">
        <div className="view-menu-server-title-section">
          <FaUtensils className="view-menu-server-title-icon" />
          <div className="view-menu-server-title-text">
            <h1 className="view-menu-server-title">Menu Serveur</h1>
            <p className="view-menu-server-subtitle">Consultez et gérez les éléments du menu</p>
          </div>
        </div>
        
        <div className="view-menu-server-controls">
          <button 
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="view-menu-server-view-toggle"
          >
            {viewMode === 'grid' ? <FaEye /> : <FaEyeSlash />}
            <span>{viewMode === 'grid' ? 'Liste' : 'Grille'}</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="view-menu-server-search-section">
        <div className="view-menu-server-search-container">
          <FaSearch className="view-menu-server-search-icon" />
          <input
            type="text"
            placeholder="Rechercher un plat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="view-menu-server-search-input"
          />
        </div>
      </div>

      {/* Category Navigation */}
      <div className="view-menu-server-categories">
        {['entrees', 'plats', 'desserts', 'boissons'].map((cat) => {
          const IconComponent = getCategoryIcon(cat);
          const isActive = categorie === cat;
          return (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`view-menu-server-category-btn ${isActive ? 'view-menu-server-category-btn-active' : ''}`}
              style={{ '--category-color': getCategoryColor(cat) }}
            >
              <IconComponent className="view-menu-server-category-icon" />
              <span className="view-menu-server-category-text">{getCategoryName(cat)}</span>
            </button>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <div className="view-menu-server-error">
          <FaExclamationTriangle className="view-menu-server-error-icon" />
          <span className="view-menu-server-error-text">{error}</span>
        </div>
      )}

      {/* No Results */}
      {searchTerm && filteredItems.length === 0 && (
        <div className="view-menu-server-no-results">
          <FaSearch className="view-menu-server-no-results-icon" />
          <h3 className="view-menu-server-no-results-title">Aucun résultat</h3>
          <p className="view-menu-server-no-results-text">
            Aucun élément trouvé pour "{searchTerm}"
          </p>
        </div>
      )}

      {/* Section Title */}
      {!searchTerm && categorie && (
        <div className="view-menu-server-section-header">
          <h2 className="view-menu-server-section-title">
            {React.createElement(getCategoryIcon(categorie), { className: "view-menu-server-section-icon" })}
            {getCategoryName(categorie)}
          </h2>
          <span className="view-menu-server-items-count">
            {items.length} élément{items.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="view-menu-server-loading">
          <FaSpinner className="view-menu-server-loading-spinner" />
          <p className="view-menu-server-loading-text">Chargement...</p>
        </div>
      )}

      {/* Items Grid/List */}
      <div className={`view-menu-server-items-container ${viewMode === 'list' ? 'view-menu-server-items-list' : 'view-menu-server-items-grid'}`}>
        {(searchTerm ? filteredItems : items).map((item) => {
          const CategoryIcon = getCategoryIcon(item.category || categorie);
          const isEditing = editingItem?.id === item.id;
          
          return (
            <div key={item.id} className="view-menu-server-item-card">
              {/* Item Header */}
              <div className="view-menu-server-item-header">
                {searchTerm && (
                  <div className="view-menu-server-item-category">
                    <CategoryIcon className="view-menu-server-item-category-icon" />
                    <span>{getCategoryName(item.category)}</span>
                  </div>
                )}
                
                <div className="view-menu-server-item-status">
                  {item.actif ? (
                    <FaToggleOn className="view-menu-server-status-icon view-menu-server-status-active" />
                  ) : (
                    <FaToggleOff className="view-menu-server-status-icon view-menu-server-status-inactive" />
                  )}
                  <span className={`view-menu-server-status-text ${item.actif ? 'view-menu-server-status-text-active' : 'view-menu-server-status-text-inactive'}`}>
                    {item.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>

              {/* Item Photo */}
              <div className="view-menu-server-item-photo">
                {item.photo ? (
                  <img
                    src={item.photo}
                    alt={item.nom}
                    className="view-menu-server-item-image"
                  />
                ) : (
                  <div className="view-menu-server-item-no-photo">
                    <FaImage className="view-menu-server-no-photo-icon" />
                    <span>Aucune photo</span>
                  </div>
                )}
              </div>

              {/* Item Content */}
              <div className="view-menu-server-item-content">
                <div className="view-menu-server-item-name-section">
                  {isEditing ? (
                    <div className="view-menu-server-edit-form">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="view-menu-server-edit-input"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit(item, 'nom');
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="view-menu-server-edit-actions">
                        <button
                          onClick={() => handleSaveEdit(item, 'nom')}
                          className="view-menu-server-save-btn"
                        >
                          <FaSave />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="view-menu-server-cancel-btn"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <h3 className="view-menu-server-item-name">{item.nom}</h3>
                  )}
                </div>

                {item.prix && (
                  <div className="view-menu-server-item-price">
                    <FaEuroSign className="view-menu-server-price-icon" />
                    <span className="view-menu-server-price-value">{item.prix.toFixed(2)} €</span>
                  </div>
                )}

                {item.description && (
                  <p className="view-menu-server-item-description">{item.description}</p>
                )}
              </div>

              {/* Item Actions */}
              <div className="view-menu-server-item-actions">
                <button
                  onClick={() => handleEditItem(item)}
                  className="view-menu-server-edit-btn"
                  disabled={isEditing}
                >
                  <FaEdit />
                  <span>Modifier</span>
                </button>
                
                <button
                  onClick={() => handleToggleActive(item)}
                  className={`view-menu-server-toggle-btn ${item.actif ? 'view-menu-server-toggle-btn-deactivate' : 'view-menu-server-toggle-btn-activate'}`}
                  disabled={loading}
                >
                  {loading ? (
                    <FaSpinner className="view-menu-server-loading-icon" />
                  ) : item.actif ? (
                    <FaToggleOff />
                  ) : (
                    <FaToggleOn />
                  )}
                  <span>{item.actif ? 'Désactiver' : 'Activer'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation Popup */}
      {showPopup && (
        <div className="view-menu-server-popup">
          <div className="view-menu-server-popup-content">
            <FaCheckCircle className="view-menu-server-popup-icon" />
            <span className="view-menu-server-popup-text">{confirmation}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewMenuServer;